/**
 * Which options a searchable picker shows for what was typed (2026-10-02).
 *
 * "The pick a vendor should be a dropdown that is searchable too, like how the
 * register field is searchable." A plain select with 106 vendors is a scroll
 * through the alphabet; typing three letters should leave three rows.
 *
 * Ranked the way a hand expects: names that start with what was typed, then
 * names with a word that starts with it ("sky" finds "Big Sky Goggles"), then
 * anything containing it — in the name or the hint under it. Within a rank the
 * options keep the order they were given in, which is already the order the
 * screen chose (A to Z, usually).
 */

export interface SelectOption {
  readonly value: string;
  readonly label: string;
  /** A second, quieter line — a vendor's code, a department's count. Searched too. */
  readonly hint?: string;
}

export function matchOptions(
  options: readonly SelectOption[],
  typed: string,
): readonly SelectOption[] {
  const wanted = typed.trim().toLowerCase();
  if (wanted === '') return options;

  const ranked: { option: SelectOption; rank: number; index: number }[] = [];
  options.forEach((option, index) => {
    const label = option.label.toLowerCase();
    const rank = label.startsWith(wanted)
      ? 0
      : label.split(/[^a-z0-9]+/).some((word) => word.startsWith(wanted))
        ? 1
        : label.includes(wanted) || (option.hint ?? '').toLowerCase().includes(wanted)
          ? 2
          : -1;
    if (rank >= 0) ranked.push({ option, rank, index });
  });
  return ranked.sort((a, b) => a.rank - b.rank || a.index - b.index).map((entry) => entry.option);
}
